/**
 * Session history. Spec 11.3: "Filterable, cursor-paginated list."
 */
import type { SessionDto } from '@lila/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SelectField } from '../../../components/ui/FieldParts';
import { Pagination } from '../../../components/ui/Pagination';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { errorKeyFor } from '../../../lib/errors';
import { useCursorPages } from '../../../lib/pagination';
import { trpcClient } from '../../../lib/trpc';

export const Route = createFileRoute('/$lang/studio/sessions/')({ component: Page });

const STATUSES = ['lobby', 'active', 'completed', 'abandoned', 'cancelled'] as const;
type StatusFilter = 'all' | (typeof STATUSES)[number];

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const { lang } = Route.useParams();

  const [status, setStatus] = useState<StatusFilter>('all');
  const pages = useCursorPages();

  const sessions = useQuery({
    queryKey: ['session', 'list', 'studio', status, pages.cursor ?? 'first'] as const,
    queryFn: () =>
      trpcClient.session.list.query({
        limit: 20,
        mode: 'facilitated',
        ...(pages.cursor === undefined ? {} : { cursor: pages.cursor }),
        ...(status === 'all' ? {} : { status }),
      }),
  });

  const columns: readonly Column<SessionDto>[] = [
    {
      id: 'title',
      header: t('studio:create.sessionTitle'),
      cell: (session) => (
        /*
         * A real link in a cell rather than a click handler on the row: focusable,
         * announced, and openable in a new tab. See the note in `DataTable`.
         */
        <Link
          to={
            session.status === 'lobby' || session.status === 'active'
              ? '/$lang/studio/sessions/$id/manage'
              : '/$lang/studio/results/$id'
          }
          params={{ lang, id: session.id }}
          className="text-accent underline-offset-4 hover:underline"
        >
          {session.title ?? t('studio:dashboard.title')}
        </Link>
      ),
    },
    {
      id: 'status',
      header: t('studio:history.status'),
      cell: (session) => (
        <Badge tone={session.status === 'active' ? 'positive' : 'neutral'}>
          {t(`common:dashboard.status.${session.status}`)}
        </Badge>
      ),
    },
    {
      id: 'players',
      header: t('studio:history.players'),
      align: 'end',
      hideOnMobile: true,
      cell: (session) => session.participants.length,
    },
    {
      id: 'started',
      header: t('studio:history.started'),
      hideOnMobile: true,
      cell: (session) =>
        session.startedAt === null
          ? '—'
          : new Date(session.startedAt).toLocaleString(i18n.language),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">{t('studio:history.title')}</h1>
        <Link to="/$lang/studio/sessions/new" params={{ lang }}>
          <Button>{t('studio:dashboard.newSession')}</Button>
        </Link>
      </header>

      <div className="max-w-xs">
        <SelectField
          label={t('studio:history.status')}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as StatusFilter);
            /* A new filter is a new list; the old cursors point into the previous one. */
            pages.reset();
          }}
          options={[
            { value: 'all', label: t('studio:history.filterAll') },
            ...STATUSES.map((value) => ({
              value,
              label: t(`common:dashboard.status.${value}`),
            })),
          ]}
        />
      </div>

      {sessions.error !== null && <Alert tone="error">{t(errorKeyFor(sessions.error))}</Alert>}

      {sessions.isLoading ? (
        <SkeletonList rows={5} label={t('studio:history.title')} />
      ) : (
        <>
          <DataTable
            caption={t('studio:history.title')}
            columns={columns}
            rows={sessions.data?.items ?? []}
            rowKey={(session) => session.id}
            empty={
              <EmptyState
                title={t('studio:history.empty')}
                description={t('studio:history.emptyHint')}
                action={
                  <Link to="/$lang/studio/sessions/new" params={{ lang }}>
                    <Button>{t('studio:dashboard.newSession')}</Button>
                  </Link>
                }
              />
            }
          />
          <Pagination
            hasPrevious={pages.hasPrevious}
            hasNext={(sessions.data?.nextCursor ?? null) !== null}
            onPrevious={pages.previous}
            onNext={() => {
              pages.next(sessions.data?.nextCursor ?? null);
            }}
            labels={{
              previous: t('common:pagination.previous'),
              next: t('common:pagination.next'),
              nav: t('studio:history.title'),
            }}
          />
        </>
      )}
    </div>
  );
}
